import i18n from "@/i18n";

import { translateRequestError } from "@/utils/errors";

/** One list the API refused to archive/delete, with its reason code (PREC-4537). */
export interface RefusedList {
	id: number;
	reason: string;
}

export type NamedList = { id: number; name: string };

/** The refused lists named with the sentence of each reason, or null when nothing was refused. */
export const listRefusalMessage = (
	refused: RefusedList[] | undefined,
	lists: readonly NamedList[] = []
): string | null => {
	if (!refused?.length) {
		return null;
	}

	return refused
		.map((refusal) => {
			const name =
				lists.find((list) => list.id === refusal.id)?.name ||
				i18n.t("errors.listIdFallback", { id: refusal.id });

			return `${name}: ${translateRequestError({ status: 400, data: { message: refusal.reason } })}`;
		})
		.join("\n");
};

/**
 * Shows the refusal the API answered with and answers whether the action was refused. A bulk
 * archive/delete answers HTTP 200 with `status: "failed"` and the refused ids, so `.unwrap()` alone
 * would close the dialog as if it had worked (PREC-4537).
 */
export const reportRefusedLists = (
	answer: { refused?: RefusedList[] } | undefined,
	notify: (message: string) => void,
	lists: readonly NamedList[] = []
): boolean => {
	const message = listRefusalMessage(answer?.refused, lists);

	if (!message) {
		return false;
	}

	notify(message);

	return true;
};

/**
 * Reports a failed list activation (PREC-4537): a stopping list keeps its own sentence, a setup
 * refusal names the step still missing, and anything else is left to the caller's generic copy.
 */
export const activationFailureMessage = (
	error: unknown,
	stoppingMessage: string
): string | null => {
	const message = (error as { data?: { message?: unknown } } | null)?.data?.message;

	if (message === "LIST_IS_STOPPING") {
		return stoppingMessage;
	}

	return typeof message === "string" && message.startsWith("LIST_SETUP_")
		? translateRequestError(error)
		: null;
};

export const reportActivationFailure = (
	error: unknown,
	stoppingMessage: string,
	notify: (message: string) => void
): void => {
	const message = activationFailureMessage(error, stoppingMessage);

	if (message) {
		notify(message);
	}
};
